import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// import { Link } from 'react-router-dom';
import { useToast } from '@/shared/contexts/ToastContext';
import { submitForm } from '@/shared/api';

const requestFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .min(10, 'Please enter a valid phone number'),
  brokerage: z
    .string()
    .min(1, 'Brokerage name is required')
    .min(2, 'Brokerage name must be at least 2 characters'),
  sendWebinar: z.boolean()
});

type RequestFormData = z.infer<typeof requestFormSchema>;

interface Hero22Props {
  title?: string;
  description?: string;
  backgroundImage?: string;
}

interface CalculatorState {
  plan: 2950 | 4950 | 8950;
  closeRate: number;
  buyerCommission: number;
  tenantCommission: number;
  fsboConversionRate: number;
}

// Plan definitions (constant, defined outside component)
const plans = [
  {
    id: 'essential',
    label: 'Essential',
    price: 2950
  },
  {
    id: 'premium',
    label: 'Premium',
    price: 4950
  },
  {
    id: 'elite',
    label: 'Elite',
    price: 8950
  }
] as const;

export const Hero22 = ({
  title = 'Made for the top-perfomers, teams and brokerages that demand growth',
  description = '40–100 buyer/rental leads per month with shared or exclusive options. One flat monthly fee.',
  backgroundImage = '/images/hero-10.jpg'
}: Hero22Props) => {
  const { notify } = useToast();

  // ROI Calculator state
  const [calcState, setCalcState] = useState<CalculatorState>({
    plan: 4950,
    closeRate: 9,
    buyerCommission: 13000,
    tenantCommission: 1150,
    fsboConversionRate: 7
  });

  // Accordion state
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isRevenueBreakdownOpen, setIsRevenueBreakdownOpen] = useState(false);

  // Get current plan based on plan value
  const currentPlan = useMemo(() => {
    return plans.find(plan => plan.price === calcState.plan) || plans[1]; // Default to Premium
  }, [calcState.plan]);

  const currentPlanLabel = currentPlan.label;

  // Calculate FSBO listings range and max based on plan tier (not price)
  const fsboListingsRange = useMemo(() => {
    if (currentPlan.id === 'essential') return { min: 3, max: 5 };
    if (currentPlan.id === 'premium') return { min: 8, max: 10 };
    return { min: 15, max: 20 }; // Elite
  }, [currentPlan.id]);

  // Lead ranges for display (6-9 leads per listing)
  const totalLeadsRange = useMemo(
    () => ({
      min: Math.floor(fsboListingsRange.min * 5),
      max: Math.floor(fsboListingsRange.max * 6)
    }),
    [fsboListingsRange]
  );

  // Calculate per lead cost range
  // const perLeadCostRange = useMemo(() => {
  //   // Maximum per lead cost = plan cost / min leads (fewer leads = higher cost per lead)
  //   const maxPerLeadCost = calcState.plan / totalLeadsRange.min;
  //   // Minimum per lead cost = plan cost / max leads (more leads = lower cost per lead)
  //   const minPerLeadCost = calcState.plan / totalLeadsRange.max;
  //   return {
  //     min: minPerLeadCost,
  //     max: maxPerLeadCost
  //   };
  // }, [calcState.plan, totalLeadsRange]);

  // Calculate min and max revenue based on ranges (using 5-6 leads per listing)
  const minRevenue = useMemo(() => {
    const minListings = fsboListingsRange.min;
    // Minimum leads: min listings × 5 leads per listing
    const minLeads = minListings * 5;
    const minBuyerLeads = minLeads * 0.3;
    const minLeaseLeads = minLeads * 0.7;
    // Conversions per month
    const minBuyerConversions = (minBuyerLeads * calcState.closeRate) / 100;
    const minLeaseConversions = (minLeaseLeads * calcState.closeRate) / 100;
    const minFsboFullService =
      (minListings * calcState.fsboConversionRate) / 100;
    // Monthly revenue
    const minBuyerRevenue = minBuyerConversions * calcState.buyerCommission;
    const minLeaseRevenue = minLeaseConversions * calcState.tenantCommission;
    const minFsboRevenue = minFsboFullService * calcState.buyerCommission;
    const minMonthlyRevenue =
      minBuyerRevenue + minLeaseRevenue + minFsboRevenue;
    // Annual revenue
    return Math.floor(minMonthlyRevenue * 12);
  }, [
    fsboListingsRange,
    calcState.closeRate,
    calcState.buyerCommission,
    calcState.tenantCommission,
    calcState.fsboConversionRate
  ]);

  const maxRevenue = useMemo(() => {
    const maxListings = fsboListingsRange.max;
    // Maximum leads: max listings × 6 leads per listing
    const maxLeads = maxListings * 6;
    const maxBuyerLeads = maxLeads * 0.3;
    const maxLeaseLeads = maxLeads * 0.7;
    // Conversions per month
    const maxBuyerConversions = (maxBuyerLeads * calcState.closeRate) / 100;
    const maxLeaseConversions = (maxLeaseLeads * calcState.closeRate) / 100;
    const maxFsboFullService =
      (maxListings * calcState.fsboConversionRate) / 100;
    // Monthly revenue
    const maxBuyerRevenue = maxBuyerConversions * calcState.buyerCommission;
    const maxLeaseRevenue = maxLeaseConversions * calcState.tenantCommission;
    const maxFsboRevenue = maxFsboFullService * calcState.buyerCommission;
    const maxMonthlyRevenue =
      maxBuyerRevenue + maxLeaseRevenue + maxFsboRevenue;
    // Annual revenue
    return Math.floor(maxMonthlyRevenue * 12);
  }, [
    fsboListingsRange,
    calcState.closeRate,
    calcState.buyerCommission,
    calcState.tenantCommission,
    calcState.fsboConversionRate
  ]);

  // Calculate annual plan cost
  const annualPlanCost = useMemo(() => calcState.plan * 12, [calcState.plan]);

  // Calculate ROI % range
  const roiRange = useMemo(
    () => ({
      min: ((minRevenue - annualPlanCost) / annualPlanCost) * 100,
      max: ((maxRevenue - annualPlanCost) / annualPlanCost) * 100
    }),
    [minRevenue, maxRevenue, annualPlanCost]
  );

  // Calculate buyer and rental revenue breakdowns (using 5-6 leads per listing)
  const buyerRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    const minLeads = minListings * 5;
    const maxLeads = maxListings * 6;
    const minBuyerLeads = minLeads * 0.3;
    const maxBuyerLeads = maxLeads * 0.3;
    // Conversions per month (using decimals for accuracy)
    const minBuyerConversions = (minBuyerLeads * calcState.closeRate) / 100;
    const maxBuyerConversions = (maxBuyerLeads * calcState.closeRate) / 100;
    // Annual revenue
    const minBuyerRevenue =
      minBuyerConversions * calcState.buyerCommission * 12;
    const maxBuyerRevenue =
      maxBuyerConversions * calcState.buyerCommission * 12;
    return {
      min: Math.floor(minBuyerRevenue),
      max: Math.floor(maxBuyerRevenue),
      minClosings: Math.floor(minBuyerConversions * 12),
      maxClosings: Math.floor(maxBuyerConversions * 12)
    };
  }, [fsboListingsRange, calcState.closeRate, calcState.buyerCommission]);

  const tenantRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    const minLeads = minListings * 5;
    const maxLeads = maxListings * 6;
    const minLeaseLeads = minLeads * 0.7;
    const maxLeaseLeads = maxLeads * 0.7;
    // Conversions per month (using decimals for accuracy)
    const minLeaseConversions = (minLeaseLeads * calcState.closeRate) / 100;
    const maxLeaseConversions = (maxLeaseLeads * calcState.closeRate) / 100;
    // Annual revenue
    const minLeaseRevenue =
      minLeaseConversions * calcState.tenantCommission * 12;
    const maxLeaseRevenue =
      maxLeaseConversions * calcState.tenantCommission * 12;
    return {
      min: Math.floor(minLeaseRevenue),
      max: Math.floor(maxLeaseRevenue),
      minClosings: Math.floor(minLeaseConversions * 12),
      maxClosings: Math.floor(maxLeaseConversions * 12)
    };
  }, [fsboListingsRange, calcState.closeRate, calcState.tenantCommission]);

  // Calculate FSBO to Full Representation revenue breakdown
  const fsboRevenueBreakdown = useMemo(() => {
    const minListings = fsboListingsRange.min;
    const maxListings = fsboListingsRange.max;
    // FSBO to Full Service conversions per month
    const minFsboFullService =
      (minListings * calcState.fsboConversionRate) / 100;
    const maxFsboFullService =
      (maxListings * calcState.fsboConversionRate) / 100;
    // Annual revenue (using buyer commission for FSBO conversions)
    const minFsboRevenue = minFsboFullService * calcState.buyerCommission * 12;
    const maxFsboRevenue = maxFsboFullService * calcState.buyerCommission * 12;
    return {
      min: Math.floor(minFsboRevenue),
      max: Math.floor(maxFsboRevenue),
      minConversions: Math.floor(minFsboFullService * 12),
      maxConversions: Math.floor(maxFsboFullService * 12)
    };
  }, [
    fsboListingsRange,
    calcState.fsboConversionRate,
    calcState.buyerCommission
  ]);

  const updateCalcState = (key: keyof CalculatorState, value: number) => {
    setCalcState(prev => ({ ...prev, [key]: value }));
  };

  // Handle plan selection
  const handlePlanSelect = (planId: string) => {
    const selectedPlan = plans.find(p => p.id === planId);
    if (selectedPlan) {
      setCalcState(prev => ({
        ...prev,
        plan: selectedPlan.price as 2950 | 4950 | 8950
      }));
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<RequestFormData>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      sendWebinar: true
    }
  });

  const handleFsboClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.hash = '#fsbo-question';

    // Find the FSBO question element
    const fsboQuestion = document.getElementById('fsbo-question');
    if (fsboQuestion) {
      // Scroll to the question
      fsboQuestion.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add highlight class (permanently - stays highlighted)
      fsboQuestion.classList.add('highlight-fsbo');
    }
  };

  const onSubmit = async (data: RequestFormData) => {
    try {
      // Submit to API to save to file
      try {
        await submitForm({
          name: data.name,
          email: data.email,
          phone: data.phone,
          brokerage: data.brokerage,
          formType: 'ready-to-get-started'
        });
      } catch (apiError) {
        // Log error but don't block the user experience
        console.error('Failed to save form submission:', apiError);
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      notify('success', `Thank you ${data.name}! We'll be in touch soon.`);
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      notify('error', 'Something went wrong. Please try again.');
    }
  };

  return (
    <section
      id="hero-22"
      className="bg--fixed hero-section z-[3] relative pt-[140px] lg:max-xl:pt-[150px] md:max-lg:pt-[80px] sm:max-md:pt-[60px] xsm:max-sm:pt-[50px] bg-scroll sm:max-md:w-auto xsm:max-sm:w-auto w-full bg-no-repeat bg-[center_center] bg-cover md:max-lg:!mt-[80px] hidden lg:block"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="container">
        {/* HEADLINE AND SUBHEADLINE - OUTSIDE CALCULATOR */}
        <div className="mb-[30px] md:max-lg:mb-[25px] sm:max-md:mb-[20px] xsm:max-sm:mb-[15px]">
          <div className="wow fadeInRight">
            <h2 className="s-36 w--700 xl:!text-[2.25rem] lg:max-xl:!text-[2rem] md:max-lg:!text-[1.875rem] sm:max-md:!text-[1.75rem] xsm:max-sm:!text-[1.5rem] !font-bold leading-[1.3] !mb-[15px] lg:max-xl:!mb-[12px] md:max-lg:!mb-[10px] sm:max-md:!mb-[10px] xsm:max-sm:!mb-[8px] font-Jakarta !text-[#353f4f]">
              {title}
            </h2>
            <p className="p-lg !text-[#757575] xl:!mb-0 lg:max-xl:!mb-0 md:max-lg:!mb-0 sm:max-md:!mb-0 xsm:max-sm:!mb-0">
              {description}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] items-start">
          {/* ROI CALCULATOR - LEFT SIDE */}
          <div className="md:w-6/12 lg:max-xl:w-7/12 xl:w-7/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
            <div className="r-10 wow fadeInRight !rounded-[10px] relative bg-white border shadow-[0px_15px_20px_0px_rgba(0,0,0,0.08)] pt-[30px] xl:!pb-[25px] px-[30px] border-solid border-[#f8f8f8] lg:max-xl:pt-[25px] lg:max-xl:!pb-5 lg:max-xl:px-[25px] md:max-lg:pt-[25px] md:max-lg:!pb-[20px] md:max-lg:px-[25px] sm:max-md:mx-[3%] sm:max-md:my-0 sm:max-md:pt-[30px] sm:max-md:!pb-[25px] sm:max-md:px-[30px] xsm:max-sm:mx-[2%] xsm:max-sm:my-0 xsm:max-sm:pt-[25px] xsm:max-sm:!pb-[20px] xsm:max-sm:px-[25px]">
              {/* Plan Selection */}
              <div className="mb-[25px] md:max-lg:mb-[22px] sm:max-md:mb-[20px] xsm:max-sm:mb-[18px]">
                <div className="mb-[16px]">
                  <label className="block text-[#353f4f] !text-[1rem] font-bold font-Jakarta sm:max-md:text-left xsm:max-sm:text-left">
                    Choose a plan
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:max-lg:gap-2.5 sm:max-md:gap-3 xsm:max-sm:gap-2.5">
                  {plans.map(plan => {
                    const isSelected = calcState.plan === plan.price;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handlePlanSelect(plan.id)}
                        className={`group relative overflow-hidden rounded-[10px] border-2 transition-all duration-[400ms] ease-[ease-in-out] cursor-pointer text-left p-4 md:max-lg:p-3.5 sm:max-md:p-4 xsm:max-sm:p-3.5 ${
                          isSelected
                            ? 'bg-gradient-to-br from-[#663ae9] to-[#5a2fd9] border-[#663ae9] shadow-[0px_8px_24px_0px_rgba(102,58,233,0.25)] scale-[1.02]'
                            : 'bg-white border-[#e0e0e0] hover:border-[#663ae9] hover:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)]'
                        }`}
                      >
                        {/* Selected indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}

                        {/* Plan Label */}
                        <div
                          className={`text-[0.8125rem] font-semibold uppercase tracking-wider mb-2 font-Jakarta ${
                            isSelected ? 'text-white/90' : 'text-[#666]'
                          }`}
                        >
                          {plan.label}
                        </div>

                        {/* First month discount badge for Essential plan */}
                        {plan.id === 'essential' && (
                          <div className="mb-2">
                            <span
                              className={`text-[0.75rem] font-semibold font-Jakarta px-2 py-1 ${
                                isSelected
                                  ? 'bg-white text-[#663ae9]'
                                  : 'bg-[#663ae9] text-white'
                              }`}
                            >
                              ✨ 50% Off
                            </span>
                          </div>
                        )}

                        {/* Price */}
                        {plan.id === 'essential' ? (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span
                                className={`text-[1.75rem] font-bold leading-none font-Jakarta md:max-lg:text-[1.625rem] sm:max-md:text-[1.75rem] xsm:max-sm:text-[1.625rem] ${
                                  isSelected ? 'text-white' : 'text-[#353f4f]'
                                }`}
                              >
                                $1,475
                              </span>
                              <span
                                className={`text-[0.875rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                /mo
                              </span>
                            </div>
                            <div className="mt-1">
                              <span
                                className={`text-[0.75rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                renews at $2,950/mo
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span
                                className={`text-[1.75rem] font-bold leading-none font-Jakarta md:max-lg:text-[1.625rem] sm:max-md:text-[1.75rem] xsm:max-sm:text-[1.625rem] ${
                                  isSelected ? 'text-white' : 'text-[#353f4f]'
                                }`}
                              >
                                ${plan.price.toLocaleString()}
                              </span>
                              <span
                                className={`text-[0.875rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                /mo
                              </span>
                            </div>
                            <div className="mt-1">
                              <span
                                className={`text-[0.75rem] font-medium font-Jakarta ${
                                  isSelected ? 'text-white/80' : 'text-[#666]'
                                }`}
                              >
                                Cancel anytime.
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Hover effect for non-selected */}
                        {!isSelected && (
                          <div className="mt-2 text-[0.75rem] text-[#999] font-medium font-Jakarta opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            Click to select →
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Trust message */}
                <div className="mt-4 flex items-center justify-center gap-2 text-center sm:max-md:justify-start xsm:max-sm:justify-start">
                  <svg
                    className="w-5 h-5 text-[#1680fb] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-[0.875rem] text-[#666] font-medium font-Jakarta">
                    No setup fee. Cancel anytime.
                  </span>
                </div>
              </div>

              {/* Summary Sentence */}
              <div className="mb-[18px] md:max-lg:mb-[16px] sm:max-md:mb-[16px] xsm:max-sm:mb-[14px] p-6 rounded-[10px] bg-gradient-to-br from-[#f0f7ff] via-[#e8f4fd] to-[#f0f9ff] border border-[#d1e9ff] shadow-sm sm:max-md:text-left xsm:max-sm:text-left">
                <h3 className="text-[#353f4f] !text-[1rem] font-bold !mb-[15px] font-Jakarta md:max-lg:!mb-[12px] sm:max-md:!mb-[12px] xsm:max-sm:!mb-[10px] sm:max-md:text-left xsm:max-sm:text-left">
                  Projected results
                </h3>
                {/* {calcState.plan === 2500 && (
                  <div className="mb-[15px] p-3 rounded-[6px] bg-[#fff3cd] border border-[#ffc107] sm:max-md:text-left xsm:max-sm:text-left">
                    <p className="text-[#856404] !text-[0.9375rem] leading-[1.6] font-Jakarta m-0 font-semibold">
                      Note: The Trial plan is for one-month only.
                    </p>
                  </div>
                )} */}
                <p className="text-[#353f4f] !text-[0.9375rem] leading-[1.8] font-Jakarta m-0 sm:max-md:text-left xsm:max-sm:text-left">
                  The {currentPlanLabel} plan generates
                  <span className="bg-[#fef3c7] px-1.5 py-0.5 rounded font-semibold">
                    {totalLeadsRange.min} to {totalLeadsRange.max} buyer/rental
                    leads per month.
                  </span>
                  <br />
                  <br />
                  Remember... these leads actually want to be contacted by an
                  agent because they requested it from a listing on realtor.ca
                  <br />
                  <br />
                  {currentPlan.id !== 'essential' && (
                    <>
                      <span className="bg-[#fef3c7] px-1.5 py-0.5 rounded font-semibold">
                        As a bonus, you would also receive{' '}
                        {fsboListingsRange.min} to {fsboListingsRange.max} FSBO
                        listing leads
                      </span>
                      from property owners who want to have thier listing posted
                      on Realtor.ca through you, without any representation.
                      This comes with the opportunity to convert them to full
                      representation.
                      <a
                        href="#fsbo-question"
                        onClick={handleFsboClick}
                        className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-700 transition-colors"
                        aria-label="Learn more about FSBO listings"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        &nbsp; What are FSBO listings?
                      </a>
                      <br />
                      <br />
                    </>
                  )}
                  Based on {totalLeadsRange.min} to {totalLeadsRange.max}{' '}
                  listings, this would produce{' '}
                  <span className="bg-[#fef3c7] px-1.5 py-0.5 rounded font-semibold">
                    ${minRevenue.toLocaleString()}-$
                    {maxRevenue.toLocaleString()} in annual commissions
                  </span>
                  . With an estimated ROI of between{' '}
                  <span className="bg-[#fef3c7] px-1.5 py-0.5 rounded font-semibold">
                    {roiRange.min.toFixed(0)}% to {roiRange.max.toFixed(0)}%
                  </span>
                  .
                  <br />
                  <br />
                  This is based on conservative conversion rates, which you can
                  adjust in the calculations section below.
                </p>
                {/* Revenue Breakdown Accordion */}
                <div className="mb-[18px] md:max-lg:mb-[16px] sm:max-md:mb-[16px] xsm:max-sm:mb-[14px] rounded sm:max-md:text-left xsm:max-sm:text-left">
                  <div className="accordion-item">
                    <div
                      className="accordion-thumb cursor-pointer pt-4 relative rounded"
                      onClick={() =>
                        setIsRevenueBreakdownOpen(!isRevenueBreakdownOpen)
                      }
                    >
                      <h5 className="text-[#353f4f] !text-[1rem] font-semibold leading-[1.4] pr-[8%] font-Jakarta sm:max-md:text-left xsm:max-sm:text-left">
                        <span
                          className="inline-block align-middle"
                          style={{ transform: 'translateY(-2px)' }}
                        >
                          ✨
                        </span>{' '}
                        &nbsp; View: Revenue breakdown
                      </h5>
                      <span
                        className={`absolute right-[2px] top-[22px] text-[1rem] transition-all duration-300 ${
                          isRevenueBreakdownOpen ? '' : ''
                        }`}
                      >
                        {isRevenueBreakdownOpen ? '−' : '+'}
                      </span>
                    </div>
                    {isRevenueBreakdownOpen && (
                      <div className="accordion-panel pt-6 pb-6 px-6 rounded-[6px] mt-2">
                        {/* Buyer Lead Revenue */}
                        <div className="mb-6 md:max-lg:mb-5 sm:max-md:mb-5 xsm:max-sm:mb-4">
                          <h6 className="text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                            Buyer Lead Revenue:{' '}
                            <span className="text-[#1680fb] !text-[1.125rem]">
                              ${buyerRevenueBreakdown.min.toLocaleString()}-$
                              {buyerRevenueBreakdown.max.toLocaleString()}
                            </span>{' '}
                            ({buyerRevenueBreakdown.minClosings}-
                            {buyerRevenueBreakdown.maxClosings} closings)
                          </h6>
                          <p className="text-[#666] !text-[0.875rem] !mb-0 leading-[1.5] font-Jakarta">
                            Revenue generated from buyer leads that successfully
                            convert to closed transactions. This is calculated
                            based on the number of buyer leads, your close rate,
                            and average buyer commission.
                          </p>
                        </div>

                        {/* Rental Lead Revenue */}
                        <div className="mb-6 md:max-lg:mb-5 sm:max-md:mb-5 xsm:max-sm:mb-4">
                          <h6 className="text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                            Rental Lead Revenue:{' '}
                            <span className="text-[#1680fb] !text-[1.125rem]">
                              ${tenantRevenueBreakdown.min.toLocaleString()}-$
                              {tenantRevenueBreakdown.max.toLocaleString()}
                            </span>{' '}
                            ({tenantRevenueBreakdown.minClosings}-
                            {tenantRevenueBreakdown.maxClosings} closings)
                          </h6>
                          <p className="text-[#666] !text-[0.875rem] !mb-0 leading-[1.5] font-Jakarta">
                            Revenue generated from tenant/lease leads that
                            successfully convert to closed lease transactions.
                            This is calculated based on the number of tenant
                            leads, your close rate, and average tenant
                            commission.
                          </p>
                        </div>

                        {/* FSBO to Full Representation */}
                        <div className="mb-0">
                          <h6 className="text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                            FSBO to Full Representation:{' '}
                            <span className="text-[#1680fb] !text-[1.125rem]">
                              ${fsboRevenueBreakdown.min.toLocaleString()}-$
                              {fsboRevenueBreakdown.max.toLocaleString()}
                            </span>{' '}
                            ({fsboRevenueBreakdown.minConversions}-
                            {fsboRevenueBreakdown.maxConversions} conversions)
                          </h6>
                          <p className="text-[#666] !text-[0.875rem] !mb-0 leading-[1.5] font-Jakarta">
                            Revenue generated when FSBO (For Sale By Owner)
                            listings convert to full-service representation.
                            This represents additional revenue opportunities
                            when property owners decide to use your full
                            brokerage services instead of selling on their own.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Lead Split Display - Compact */}
              {/* <div className="mb-[18px] md:max-lg:mb-[16px] sm:max-md:mb-[16px] xsm:max-sm:mb-[14px]">
                <label className="block text-[#353f4f] !text-[0.9375rem] font-semibold !mb-[8px] font-Jakarta">
                  Lead Split (30% Buyers / 70% Tenants)
                </label>
                <div className="grid grid-cols-2 gap-3 md:max-lg:gap-2 sm:max-md:gap-2 xsm:max-sm:gap-2">
                  <div className="p-3 bg-[#f5f6f8] rounded-[6px]">
                    <div className="text-[#666] !text-[0.8125rem] !mb-1">Buyer Leads</div>
                    <div className="text-[#1680fb] !text-[1.125rem] font-bold font-Jakarta">
                      0
                    </div>
                  </div>
                  <div className="p-3 bg-[#f5f6f8] rounded-[6px]">
                    <div className="text-[#666] !text-[0.8125rem] !mb-1">Lease Leads</div>
                    <div className="text-[#1680fb] !text-[1.125rem] font-bold font-Jakarta">
                      0
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Calculations Accordion */}
              <div className="mb-[18px] md:max-lg:mb-[16px] sm:max-md:mb-[16px] xsm:max-sm:mb-[14px] bg-[#f5f6f8] sm:max-md:text-left xsm:max-sm:text-left">
                <div className="accordion-item">
                  <div
                    className="accordion-thumb cursor-pointer pt-4 pb-[20px] px-4 relative bg-[#f5f6f8] rounded"
                    onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                  >
                    <h5 className="text-[#353f4f] !text-[1rem] font-semibold leading-[1.4] pr-[8%] font-Jakarta sm:max-md:text-left xsm:max-sm:text-left">
                      <span
                        className="inline-block align-middle"
                        style={{ transform: 'translateY(-2px)' }}
                      >
                        ✨
                      </span>{' '}
                      &nbsp; Adjust: Calculations
                    </h5>
                    <span
                      className={`absolute right-[18px] top-[22px] text-[1rem] transition-all duration-300 ${
                        isAccordionOpen ? '' : ''
                      }`}
                    >
                      {isAccordionOpen ? '−' : '+'}
                    </span>
                  </div>
                  {isAccordionOpen && (
                    <div className="accordion-panel pt-6 pb-6 px-6 rounded-[6px] mt-2">
                      {/* Close Rate Slider */}
                      <div className="mb-6 md:max-lg:mb-5 sm:max-md:mb-5 xsm:max-sm:mb-4">
                        <label className="block text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                          Close Rate per Lead:{' '}
                          <span className="text-[#1680fb] !text-[1.125rem]">
                            {calcState.closeRate}%
                          </span>
                        </label>
                        <p className="text-[#666] !text-[0.875rem] !mb-3 leading-[1.5] font-Jakarta">
                          The percentage of leads that successfully convert into
                          closed deals. Industry average ranges from 7% to 15%.
                        </p>
                        <input
                          type="range"
                          min="7"
                          max="15"
                          step="0.5"
                          value={calcState.closeRate}
                          onChange={e =>
                            updateCalcState(
                              'closeRate',
                              parseFloat(e.target.value)
                            )
                          }
                          className="w-full h-3 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, #1680fb 0%, #1680fb ${((calcState.closeRate - 7) / 8) * 100}%, #e0e0e0 ${((calcState.closeRate - 7) / 8) * 100}%, #e0e0e0 100%)`
                          }}
                        />
                        <div className="flex justify-between text-[#666] !text-[0.75rem] mt-2 font-medium">
                          <span>7%</span>
                          <span>15%</span>
                        </div>
                      </div>

                      {/* Buyer Commission Slider */}
                      <div className="mb-6 md:max-lg:mb-5 sm:max-md:mb-5 xsm:max-sm:mb-4">
                        <label className="block text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                          Avg Commission (Buyers):{' '}
                          <span className="text-[#1680fb] !text-[1.125rem]">
                            ${calcState.buyerCommission.toLocaleString()}
                          </span>
                        </label>
                        <p className="text-[#666] !text-[0.875rem] !mb-3 leading-[1.5] font-Jakarta">
                          Average commission earned per successful buyer
                          transaction. Typical range is $5,000 to $20,000
                          depending on property value and market.
                        </p>
                        <input
                          type="range"
                          min="5000"
                          max="20000"
                          step="500"
                          value={calcState.buyerCommission}
                          onChange={e =>
                            updateCalcState(
                              'buyerCommission',
                              parseInt(e.target.value)
                            )
                          }
                          className="w-full h-3 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, #1680fb 0%, #1680fb ${((calcState.buyerCommission - 5000) / 15000) * 100}%, #e0e0e0 ${((calcState.buyerCommission - 5000) / 15000) * 100}%, #e0e0e0 100%)`
                          }}
                        />
                        <div className="flex justify-between text-[#666] !text-[0.75rem] mt-2 font-medium">
                          <span>$5,000</span>
                          <span>$20,000</span>
                        </div>
                      </div>

                      {/* Rental Commission Slider */}
                      <div className="mb-6 md:max-lg:mb-5 sm:max-md:mb-5 xsm:max-sm:mb-4">
                        <label className="block text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                          Avg Commission (Tenants):{' '}
                          <span className="text-[#1680fb] !text-[1.125rem]">
                            ${calcState.tenantCommission.toLocaleString()}
                          </span>
                        </label>
                        <p className="text-[#666] !text-[0.875rem] !mb-3 leading-[1.5] font-Jakarta">
                          Average commission earned per successful tenant/lease
                          transaction. Typically ranges from $500 to $2,500 per
                          lease.
                        </p>
                        <input
                          type="range"
                          min="500"
                          max="2500"
                          step="50"
                          value={calcState.tenantCommission}
                          onChange={e =>
                            updateCalcState(
                              'tenantCommission',
                              parseInt(e.target.value)
                            )
                          }
                          className="w-full h-3 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, #1680fb 0%, #1680fb ${((calcState.tenantCommission - 500) / 2000) * 100}%, #e0e0e0 ${((calcState.tenantCommission - 500) / 2000) * 100}%, #e0e0e0 100%)`
                          }}
                        />
                        <div className="flex justify-between text-[#666] !text-[0.75rem] mt-2 font-medium">
                          <span>$500</span>
                          <span>$2,500</span>
                        </div>
                      </div>

                      {/* FSBO Conversion Rate Slider */}
                      <div className="mb-0">
                        <label className="block text-[#353f4f] !text-[1rem] font-semibold !mb-2 font-Jakarta">
                          FSBO to Full Service:{' '}
                          <span className="text-[#1680fb] !text-[1.125rem]">
                            {calcState.fsboConversionRate}%
                          </span>
                        </label>
                        <p className="text-[#666] !text-[0.875rem] !mb-3 leading-[1.5] font-Jakarta">
                          The percentage of FSBO (For Sale By Owner) listings
                          that convert to full-service listings. Industry
                          average is typically between 6% and 9%.
                        </p>
                        <input
                          type="range"
                          min="6"
                          max="9"
                          step="0.5"
                          value={calcState.fsboConversionRate}
                          onChange={e =>
                            updateCalcState(
                              'fsboConversionRate',
                              parseFloat(e.target.value)
                            )
                          }
                          className="w-full h-3 bg-[#e0e0e0] rounded-lg appearance-none cursor-pointer slider"
                          style={{
                            background: `linear-gradient(to right, #1680fb 0%, #1680fb ${((calcState.fsboConversionRate - 6) / 3) * 100}%, #e0e0e0 ${((calcState.fsboConversionRate - 6) / 3) * 100}%, #e0e0e0 100%)`
                          }}
                        />
                        <div className="flex justify-between text-[#666] !text-[0.75rem] mt-2 font-medium">
                          <span>6%</span>
                          <span>9%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* END ROI CALCULATOR */}

          {/* HERO REQUEST FORM - RIGHT SIDE */}
          <div className="hidden md:block md:w-6/12 lg:max-xl:w-5/12 xl:w-5/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
            <div
              id="hero-22-form"
              className="r-10 !rounded-[10px] wow fadeInLeft relative z-[999] bg-white border shadow-[0px_15px_20px_0px_rgba(0,0,0,0.08)] pt-[45px] xl:!pb-[35px] px-[35px] border-solid border-[#f8f8f8] lg:max-xl:pt-[38px] lg:max-xl:!pb-7 lg:max-xl:px-[25px] md:max-lg:pt-[35px] md:max-lg:!pb-[25px] md:max-lg:px-[25px] sm:max-md:!text-center sm:max-md:mx-[3%] sm:max-md:my-0 sm:max-md:pt-[45px] sm:max-md:!pb-[35px] sm:max-md:px-[40px] xsm:max-sm:!text-center xsm:max-sm:mx-[2%] xsm:max-sm:my-0 xsm:max-sm:pt-[35px] xsm:max-sm:!pb-[25px] xsm:max-sm:px-[25px]"
            >
              {/* Hero Logo */}
              {/* <div className="hero-logo mb--30 !mb-[30px] lg:max-xl:!mb-[20px] md:max-lg:!mb-[20px] sm:max-md:hidden xsm:max-sm:hidden">
                <img
                  className="img-fluid w-auto max-w-[inherit] !max-h-[36px] lg:max-xl:!max-h-[30px] md:max-lg:!max-h-[28px] sm:max-md:!max-h-[34px] xsm:max-sm:!max-h-[34px]"
                  src="/images/logo-blue.png"
                  alt="hero-logo"
                />
              </div> */}
              {/* Form Image */}
              <div className="mb-[25px] lg:max-xl:mb-[20px] md:max-lg:mb-[20px] sm:max-md:mb-[20px] xsm:max-sm:mb-[18px]">
                <img
                  src="/images/form-img.png"
                  alt="Form illustration"
                  className="w-full h-[175px] object-cover rounded-[10px]"
                />
              </div>
              {/* Title */}
              <h4 className="s-26 w--700 leading-[1.4] xl:!mb-[20px] lg:max-xl:!mb-[15px] text-[1.625rem] md:max-lg:!text-[1.65rem] md:max-lg:!mb-[15px] sm:max-md:!text-[2rem] sm:max-md:!mb-[16px] sm:max-md:!p-0 xsm:max-sm:!text-[1.6875rem] xsm:max-sm:!mb-[15px] xsm:max-sm:!p-0 !font-bold font-Jakarta">
                Ready to get started?
              </h4>

              {/* Text */}
              <p className="p-md color--grey !text-[#757575] xl:!mb-[25px] md:max-lg:!text-[1.1rem] md:max-lg:!mb-[15px] sm:max-md:!text-[1.175rem] sm:max-md:!mb-[25px] sm:max-md:!p-[0_3%] xsm:max-sm:!mb-[16px] xsm:max-sm:!p-[0_5%]">
                Schedule a live demo with an expert to learn how BrokerMax can
                help you grow your business.
              </p>

              {/* Live Demo CTA */}
              {/* <div className="mb-[25px] lg:max-xl:mb-[20px] md:max-lg:mb-[20px] sm:max-md:mb-[20px] xsm:max-sm:mb-[18px]">
                <Link
                  to="/demo"
                  className="group inline-flex items-center gap-2 px-4 py-3 rounded-[8px] bg-gradient-to-r from-[#e8f4fd] to-[#d1e9ff] border border-[#b8dfff] hover:from-[#d1e9ff] hover:to-[#b8dfff] hover:border-[#1680fb] transition-all duration-200 text-[#1680fb] font-semibold text-[0.9375rem] font-Jakarta"
                >
                  <svg
                    className="w-5 h-5 transition-transform duration-200 group-hover:scale-110"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Or, Book a Live Demo with an Expert →</span>
                  <svg
                    className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div> */}

              {/* Form */}
              <form
                name="requestForm"
                className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] request-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Form Input */}
                <div className="w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
                  <input
                    type="text"
                    {...register('name')}
                    className={`form-control name block w-full leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] !mb-[22px] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] placeholder:text-[#666] ${
                      errors.name ? '!border-accent-error' : ''
                    }`}
                    placeholder="Name"
                    autoComplete="off"
                  />
                  {errors.name && (
                    <p className="text-sm text-accent-error mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                {/* Form Input */}
                <div className="w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
                  <input
                    type="email"
                    {...register('email')}
                    className={`form-control email block w-full leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] !mb-[22px] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] placeholder:text-[#666] ${
                      errors.email ? '!border-accent-error' : ''
                    }`}
                    placeholder="Email address"
                    autoComplete="off"
                  />
                  {errors.email && (
                    <p className="text-sm text-accent-error mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                {/* Form Input - Phone */}
                {/* <div className="w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
                  <input
                    type="tel"
                    {...register('phone')}
                    className={`form-control phone block w-full leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] !mb-[22px] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] placeholder:text-[#666] ${
                      errors.phone ? '!border-accent-error' : ''
                    }`}
                    placeholder="Phone number"
                    autoComplete="off"
                  />
                  {errors.phone && (
                    <p className="text-sm text-accent-error mt-1">
                      {errors.phone.message}
                    </p>
                  )}
                </div> */}
                {/* Webinar Checkbox */}
                {/* <div className="w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full mb-[22px]">
                  <label className="flex items-start cursor-pointer group p-4 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9ff] rounded-[6px] border border-[#b8dfff] hover:from-[#d1e9ff] hover:to-[#b8dfff] transition-all duration-200">
                    <input
                      type="checkbox"
                      {...register('sendWebinar')}
                      className="w-5 h-5 mt-0.5 text-[#1680fb] border-2 border-[#1680fb] focus:ring-2 focus:ring-[#1680fb] focus:ring-offset-2 cursor-pointer transition-all duration-200 accent-[#1680fb] flex-shrink-0"
                    />
                    <span className="ml-3 text-[#353f4f] !text-[0.9375rem] leading-[1.5] font-Jakarta">
                      We&apos;ll email you the webinar:{' '}
                      <span className="inline-flex items-center gap-1.5">
                        <strong className="italic">
                          &quot;How an Ontario real estate agent generated 584
                          buyer and rental leads from 177 listings in 6 months
                          from July 2025 to January 2026&quot;
                        </strong>
                      </span>
                    </span>
                  </label>
                </div> */}
                {/* Form Button */}
                <div className="w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full form-btn">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn--theme hover--theme submit w-full xl:!h-[58px] p-[16px] lg:max-xl:!h-[54px] lg:max-xl:!p-[16px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:!p-[16px] sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:!p-[16px]"
                  >
                    {isSubmitting ? 'Submitting...' : 'Schedule Call'} &#187;
                  </button>
                </div>
                {/* Form Message */}
                <div className="md:w-full w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full request-form-msg !text-center">
                  <span className="loading"></span>
                </div>
              </form>
              <hr className="my-4 border-t-2 border-gray-500" />
              {/* Testimonial Box */}
              <div className="rounded-lg p-2 relative overflow-hidden">
                {/* Yellow-orange background section on the right */}

                {/* Content wrapper with padding to avoid image */}
                <div className="relative">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-[#ffd700]"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="p-md !text-[1.0625rem] !leading-[1.6] color--black mb-4 italic">
                    &ldquo;
                    <span className="font-bold">
                      {' '}
                      With BrokerMax, I always have at least 30 listings up
                      every month, even in the &quot;slow&quot; winter. But the
                      real magic is the buyer and rental leads... Search my name
                      on REALTOR.ca and see my active listings for yourself.
                    </span>
                    &rdquo;
                  </p>

                  {/* Attribution */}
                  <p
                    data-nosnippet
                    className="!text-[0.9375rem] !font-semibold color--black text-left"
                  >
                    - Shamairah Noufaro, Real estate agent
                  </p>
                </div>
              </div>
              <hr className="my-4 border-t-2 border-gray-500" />
            </div>
            {/* END FORM */}
          </div>
          {/* END HERO REQUEST FORM */}
        </div>
        {/* End row */}
      </div>
      {/* End container */}
      {/* WAVE SHAPE BOTTOM */}
      <div className="wave-shape-bottom !absolute w-full left-0 bottom-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 265">
          <path
            fillOpacity="1"
            d="M0,256L120,256C240,256,480,256,720,240C960,224,1200,192,1320,176L1440,160L1440,320L1320,320C1200,320,960,320,720,320C480,320,240,320,120,320L0,320Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};
